<template>
  <section
    class="contacts-table-wrap bg-white dark:bg-slate-900 flex-1 h-full overflow-hidden"
  >
    <!-- Loading Overlay - appears on top when loading -->
    <div v-if="isLoading" class="contacts-table-loader-overlay">
      <div class="items-center flex flex-col text-base justify-center h-full w-full">
        <spinner size="164" />
        <span class="m-5 font-bold text-xl">{{ $t('CONTACTS_PAGE.LIST.LOADING_MESSAGE') }}</span>
      </div>
    </div>

    <!-- Vue Tables 2 -->
    <div v-if="contacts.length > 0" ref="tableContainer" class="table-container">
      <v-client-table
        :key="`table-${sortUpdateKey}`"
        ref="vuetable"
        :columns="filteredColumnKeys"
        :data="tableData"
        :options="tableOptions"
      >
      <!-- Custom Header Slots - Text is non-clickable, icon triggers server-side sorting -->
      <template v-for="column in sortableColumns" :slot="`h__${column}`">
        <div :key="column" class="custom-header-wrapper">
          <span class="header-text">{{ getColumnTitle(column) }}</span>
          <span 
            class="sort-icon-wrapper" 
            :class="{ 'is-sorted-asc': currentSortParam === column && currentSortOrder === 'asc', 'is-sorted-desc': currentSortParam === column && currentSortOrder === 'desc' }"
            @click="triggerSort(column)"
          >
            <span class="sort-icon"></span>
          </span>
        </div>
      </template>
      <!-- Name Column with Checkbox on Hover -->
      <template slot="name" slot-scope="props">
        <div 
          class="name-cell-wrapper"
          @mouseenter="onRowHover(props.row.id)"
          @mouseleave="onRowLeave(props.row.id)"
        >
          <!-- Checkbox shown on hover or when selected -->
          <label
            v-if="isRowHovered(props.row.id) || isContactSelected(props.row.id)"
            class="checkbox-wrapper"
            @click.stop
          >
            <input
              :checked="isContactSelected(props.row.id)"
              class="checkbox"
              type="checkbox"
              @change="toggleContactSelection(props.row.id)"
            />
          </label>
          <!-- Thumbnail shown when not hovered and not selected -->
          <Thumbnail
            v-else
            :src="props.row.thumbnail"
            size="32px"
            :username="props.row.name"
            :status="props.row.availability_status"
            class="contact-thumbnail"
          />
          <woot-button variant="clear" class="name-cell-button" @click="onClickContact(props.row.id)">
            <div class="user-block">
              <h6
                class="sub-block-title overflow-hidden whitespace-nowrap text-ellipsis text-slate-400 text-sm blue-color user-name min-w-0 flex-1"
                v-tooltip="getCellTooltip(`${props.row.id}-name`)"
                v-tooltip-when-truncated="{ text: props.row.name, key: `${props.row.id}-name` }"
              >
                {{ contactDisplayName(props.row.name) }}
              </h6>
              <lead-quality-crown
                v-if="accountId"
                :account-id="accountId"
                :metadata="props.row.lead_quality_metadata"
              />
              <lead-response-watch
                :waiting-since-unix="props.row.response_wait_waiting_since"
                class="lead-response-watch--in-name-cell flex-shrink-0"
              />
            </div>
          </woot-button>
        </div>
      </template>

      <!-- Email Column -->
      <template slot="email" slot-scope="props">
        <div
          v-if="props.row.email"
          class=" background rounded-xl overflow-hidden whitespace-nowrap text-ellipsis text-woot-500 dark:text-woot-500"
          v-tooltip="getCellTooltip(`${props.row.id}-email`)"
          v-tooltip-when-truncated="{ text: props.row.email, key: `${props.row.id}-email` }"
        >
          <a
            :href="`mailto:${props.row.email}`"
            rel="noopener noreferrer nofollow"
          >
            {{ props.row.email }}
          </a>
        </div>
        <span v-else>---</span>
      </template>

      <!-- Phone Number Column -->
      <template slot="phone_number" slot-scope="props">
        <div 
          v-if="props.row.phone_number" 
          class="rounded-xl background overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-phone_number`)"
          v-tooltip-when-truncated="{ text: props.row.phone_number, key: `${props.row.id}-phone_number` }"
        >
          {{ props.row.phone_number }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Created At Column -->
      <template slot="created_at" slot-scope="props">
        <div
          v-if="props.row.created_at"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          :class="{ 'text-woot-500 dark:text-woot-500': props.row.is_reengaged }"
          v-tooltip="
            props.row.is_reengaged
              ? buildReengagementTooltip(props.row)
              : getCellTooltip(`${props.row.id}-created_at`)
          "
          v-tooltip-when-truncated="{
            text: props.row.created_timing,
            key: `${props.row.id}-created_at`
          }"
        >
          {{ props.row.created_at }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Assigned To Column -->
      <template slot="assigned_to" slot-scope="props">
        <div
         v-if="props.row.assignee"
         class="overflow-hidden whitespace-nowrap text-ellipsis"
         v-tooltip="getCellTooltip(`${props.row.id}-assigned_to`)"
         v-tooltip-when-truncated="{ text: props.row.assignee.name, key: `${props.row.id}-assigned_to` }"
        >
          {{ props.row.assignee.name }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Source Column -->
      <template slot="source" slot-scope="props">
        <div
          v-if="props.row.source"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-source`)"
          v-tooltip-when-truncated="{ text: props.row.source, key: `${props.row.id}-source` }"
        >
          {{ props.row.source }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Branch Column -->
      <template slot="branch" slot-scope="props">
        <div
          v-if="props.row.branch"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-branch`)"
          v-tooltip-when-truncated="{ text: props.row.branch, key: `${props.row.id}-branch` }"
        >
          {{ props.row.branch }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Company Column -->
      <template slot="company" slot-scope="props">
        <div
          v-if="props.row.company"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-company`)"
          v-tooltip-when-truncated="{ text: props.row.company, key: `${props.row.id}-company` }"
        >
          {{ props.row.company }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- City Column -->
      <template slot="city" slot-scope="props">
        <div
          v-if="props.row.city"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-city`)"
          v-tooltip-when-truncated="{ text: props.row.city, key: `${props.row.id}-city` }"
        >
          {{ props.row.city }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Board Column -->
      <template slot="board" slot-scope="props">
        <div class="ruby-div">
          <div
            v-if="props.row.board_name && props.row.board_name != null"
            class="status-board rounded-xl cursor-pointer overflow-hidden whitespace-nowrap text-ellipsis"
            v-tooltip="getCellTooltip(`${props.row.id}-board`)"
            v-tooltip-when-truncated="{ text: props.row.board_name, key: `${props.row.id}-board` }"
            @click="visibleBoard(props.row.board_id, props.row.id, $event)"
          >
            {{ props.row.board_name }}
          </div>
          <div
            v-else
            class="state-color rounded-xl cursor-pointer"
            @click="visibleBoard(props.row.board_id, props.row.id, $event)"
          >
            Assign Board
          </div>
        </div>
      </template>

      <!-- Status/Pipeline Column -->
      <template slot="status" slot-scope="props">
        <div class="ruby-div">
          <div
            v-if="props.row.status_name && props.row.status_name !== null"
            class="status-color rounded-xl cursor-pointer overflow-hidden whitespace-nowrap text-ellipsis"
            v-tooltip="getCellTooltip(`${props.row.id}-status`)"
            v-tooltip-when-truncated="{ text: props.row.status_name, key: `${props.row.id}-status` }"
            @click="visibleStatus(props.row.status_id, props.row.board_id, props.row.id, $event)"
          >
            {{ props.row.status_name }}
          </div>
          <div
            v-else
            class="state-color rounded-xl cursor-pointer"
            @click="visibleStatus(props.row.status_id, props.row.board_id, props.row.id, $event)"
          >
            Assign Status
          </div>
        </div>
      </template>

      <!-- State Column -->
      <template slot="state" slot-scope="props">
        <div class="ruby-div">
          <div
            v-if="props.row.state_name && props.row.state_name !== null"
            class="status-color rounded-xl cursor-pointer overflow-hidden whitespace-nowrap text-ellipsis"
            v-tooltip="getCellTooltip(`${props.row.id}-state`)"
            v-tooltip-when-truncated="{ text: props.row.state_name, key: `${props.row.id}-state` }"
            @click="visibleState(props.row.state_id, props.row.id, $event)"
          >
            {{ props.row.state_name }}
          </div>
          <div
            v-else
            class="state-color rounded-xl cursor-pointer"
            @click="visibleState(props.row.state_id, props.row.id, $event)"
          >
            Assign State
          </div>
        </div>
      </template>

      <!-- Country Column -->
      <template slot="country" slot-scope="props">
        <div
          v-if="props.row.country"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-country`)"
          v-tooltip-when-truncated="{ text: `${getCountryFlag(props.row.countryCode)} ${props.row.country}`, key: `${props.row.id}-country` }"
        >
          {{ getCountryFlag(props.row.countryCode) }} {{ props.row.country }}
        </div>
        <span v-else>---</span>
      </template>

      <!-- Social/Remark Column -->
      <template slot="remark" slot-scope="props">
        <div
          v-if="hasVisibleSocialProfiles(props.row.profiles)"
          class="cell--social-profiles flex gap-0.5 items-center"
        >
          <template v-for="profile in Object.keys(props.row.profiles)">
            <a
              v-if="props.row.profiles[profile]"
              :key="profile"
              :href="`https://${profile}.com/${props.row.profiles[profile]}`"
              target="_blank"
              rel="noopener noreferrer nofollow"
              v-tooltip="profile"
            >
              <FluentIcon
                :icon="`brand-${profile}`"
                :class="profile === 'twitter' ? 'transform translate-x-[15%] translate-y-[15%] h-6 w-6' : ''"
              />
            </a>
          </template>
        </div>
        <span v-else>---</span>
      </template>

      <!-- Custom attribute columns (headers use default vue-tables headings; no sort controls) -->
      <template v-for="customCol in customAttributeColumns" :slot="customCol.key" slot-scope="props">
        <div
          :key="customCol.key"
          v-if="props.row[customCol.key]"
          class="overflow-hidden whitespace-nowrap text-ellipsis"
          v-tooltip="getCellTooltip(`${props.row.id}-${customCol.key}`)"
          v-tooltip-when-truncated="{ text: formatCustomAttributeValue(props.row[customCol.key], customCol.attributeType), key: `${props.row.id}-${customCol.key}` }"
        >
          {{ formatCustomAttributeValue(props.row[customCol.key], customCol.attributeType) }}
        </div>
        <span v-else :key="`${customCol.key}-empty`">---</span>
      </template>

      <!-- Actions Column -->
      <template slot="actions" slot-scope="props">
        <div class="relative">
          <div>
            <button
              :ref="`button_${props.row.id}`"
              class="cursor-pointer"
              v-tooltip="'More Details'"
              @click="toggleModal(props.row)"
            >
              ⋮
            </button>
          </div>

          <div
            v-if="visibleActionsRowId === props.row.id"
            :ref="`dropdown_${props.row.id}`"
            class="flex dropdown-position"
          >
            <!-- Comment button -->
            <button
              class="cursor-pointer button-background"
              v-tooltip="'Conversation'"
              @click.stop="openNewConversationModal(props.row)"
            >
              <svg
                id="comment-o"
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 21.977 18.837"
              >
                <path
                  id="Path_2456"
                  data-name="Path 2456"
                  d="M10.988,1.57a12.718,12.718,0,0,0-4.679.852,8.8,8.8,0,0,0-3.458,2.3A4.635,4.635,0,0,0,1.57,7.849a4.476,4.476,0,0,0,.877,2.618,7.474,7.474,0,0,0,2.471,2.152l1.067.613L5.654,14.41A8.794,8.794,0,0,1,4.8,16.519a12.1,12.1,0,0,0,3.372-2.1l.527-.466.7.074a13.878,13.878,0,0,0,1.594.1,12.718,12.718,0,0,0,4.679-.852,8.8,8.8,0,0,0,3.458-2.3,4.635,4.635,0,0,0,1.282-3.127,4.635,4.635,0,0,0-1.282-3.127,8.8,8.8,0,0,0-3.458-2.3A12.718,12.718,0,0,0,10.988,1.57ZM21.976,7.849A6.117,6.117,0,0,1,20.5,11.791a9.939,9.939,0,0,1-4,2.857A14.256,14.256,0,0,1,10.988,15.7a16.773,16.773,0,0,1-1.778-.1,13.4,13.4,0,0,1-5.641,2.968,9.24,9.24,0,0,1-1.4.27H2.109a.49.49,0,0,1-.331-.129.625.625,0,0,1-.2-.337v-.012a.161.161,0,0,1-.006-.147.328.328,0,0,0,.025-.123q-.006-.025.055-.117l.074-.11.086-.1.1-.11q.086-.1.38-.423l.423-.466q.129-.141.38-.484a4.985,4.985,0,0,0,.4-.625q.147-.282.331-.724a7.8,7.8,0,0,0,.319-.932,9,9,0,0,1-3.035-2.7A5.95,5.95,0,0,1,0,7.849,6.117,6.117,0,0,1,1.472,3.906a9.939,9.939,0,0,1,4-2.857A14.256,14.256,0,0,1,10.988,0a14.256,14.256,0,0,1,5.519,1.049,9.939,9.939,0,0,1,4,2.857A6.117,6.117,0,0,1,21.976,7.849Z"
                  fill="#6b6b6b"
                />
                <line
                  id="Line_271"
                  data-name="Line 271"
                  x2="9"
                  transform="translate(6.5 6.5)"
                  fill="none"
                  stroke="#707070"
                  stroke-linecap="round"
                  stroke-width="1"
                />
                <line
                  id="Line_272"
                  data-name="Line 272"
                  x2="9"
                  transform="translate(6.5 9.5)"
                  fill="none"
                  stroke="#707070"
                  stroke-linecap="round"
                  stroke-width="1"
                />
              </svg>
            </button>

            <!-- Call button -->
            <button
              class="cursor-pointer button-background"
              v-tooltip="'Call'"
              @click="startupClient(props.row)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 18 17.947"
              >
                <path
                  id="bee4d62d12dd008a6ab2be9c9ad4c247"
                  d="M17.751,11.939a2.842,2.842,0,0,1-.605-.108,8.526,8.526,0,0,1-1.183-.352,1.806,1.806,0,0,0-2.24.9l-.2.406a11,11,0,0,1-2.4-1.806,11,11,0,0,1-1.806-2.4l.379-.253a1.806,1.806,0,0,0,.9-2.24A9.33,9.33,0,0,1,10.245,4.9c-.045-.2-.081-.406-.108-.614A2.71,2.71,0,0,0,7.427,2.04H4.717a2.71,2.71,0,0,0-2.71,3.08A17.162,17.162,0,0,0,16.929,19.987h.343a2.7,2.7,0,0,0,2.71-2.719v-2.71A2.71,2.71,0,0,0,17.751,11.939Zm.452,5.419a.9.9,0,0,1-.307.677.957.957,0,0,1-.741.226A15.355,15.355,0,0,1,3.868,4.912a.985.985,0,0,1,.226-.741.9.9,0,0,1,.677-.307h2.71a.9.9,0,0,1,.9.714q.054.37.135.732a10.044,10.044,0,0,0,.415,1.4L7.671,7.3a.9.9,0,0,0-.443,1.2,13.088,13.088,0,0,0,6.323,6.323.957.957,0,0,0,1.2-.47l.56-1.265a12.365,12.365,0,0,0,1.427.415q.361.081.732.135a.9.9,0,0,1,.714.9Z"
                  transform="translate(-1.982 -2.04)"
                  fill="#6b6b6b"
                />
              </svg>
            </button>

            <!-- Edit button -->
            <button
              class="cursor-pointer button-background"
              v-tooltip="'Edit'"
              @click="OpenEditContact(props.row)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 18.713 18.715"
              >
                <path
                  id="svg_55"
                  d="M19.06,2.862a3.209,3.209,0,0,1,0,4.536L8.331,18.129a2.017,2.017,0,0,1-.9.52L2.849,19.9a.672.672,0,0,1-.825-.826l1.251-4.586a2.017,2.017,0,0,1,.52-.9L14.524,2.862a3.209,3.209,0,0,1,4.536,0m-5.406,2.77L4.745,14.543a.672.672,0,0,0-.173.3l-.941,3.451,3.451-.941a.672.672,0,0,0,.3-.175L16.29,8.267Zm1.82-1.82-.87.87L17.24,7.317l.87-.87a1.864,1.864,0,1,0-2.636-2.636"
                  transform="translate(-1.745 -1.464)"
                  fill="#6b6b6b"
                  stroke="#6b6b6b"
                  stroke-width="0.5"
                />
              </svg>
            </button>

            <!-- Delete button -->
            <button
              class="cursor-pointer button-background"
              v-tooltip="'Delete'"
              @click="toggleDeleteModal(props.row)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 17.125 19.979"
              >
                <g id="delete" transform="translate(-24 -12)">
                  <path
                    id="Path_2446"
                    data-name="Path 2446"
                    d="M72,72h1.427v8.562H72Z"
                    transform="translate(-42.292 -52.865)"
                    fill="#6b6b6b"
                  />
                  <path
                    id="Path_2447"
                    data-name="Path 2447"
                    d="M108,72h1.427v8.562H108Z"
                    transform="translate(-74.011 -52.865)"
                    fill="#6b6b6b"
                  />
                  <path
                    id="Path_2448"
                    data-name="Path 2448"
                    d="M24,36v1.427h1.427V51.7a1.427,1.427,0,0,0,1.427,1.427H38.27A1.427,1.427,0,0,0,39.7,51.7V37.427h1.427V36Zm2.854,15.7V37.427H38.27V51.7Z"
                    transform="translate(0 -21.146)"
                    fill="#6b6b6b"
                  />
                  <path
                    id="Path_2449"
                    data-name="Path 2449"
                    d="M72,12h5.708v1.427H72Z"
                    transform="translate(-42.292 0)"
                    fill="#6b6b6b"
                  />
                </g>
              </svg>
            </button>
          </div>
        </div>
      </template>
    </v-client-table>
    
    <!-- Modals inside scroll container for smooth scrolling -->
    <edit-state
      ref="editStateModal"
      :show="showState"
      :state_id="stateId"
      :contactId="contactId"
      :top="editBoardPosition.top"
      :left="editBoardPosition.left"
      :currentScrollLeft="editBoardPosition.currentScrollLeft"
      :currentScrollTop="editBoardPosition.currentScrollTop"
      @cancel="hideState"
    />

    <edit-status
      ref="editStatusModal"
      :show="showStatus"
      :status_id="statusId"
      :board_id="boardId"
      :contactId="contactId"
      :top="editBoardPosition.top"
      :left="editBoardPosition.left"
      :currentScrollLeft="editBoardPosition.currentScrollLeft"
      :currentScrollTop="editBoardPosition.currentScrollTop"
      @cancel="hideStatus"
    />

    <edit-board
      ref="editBoardModal"
      :show="showBoard"
      :board_id="boardId"
      :contactId="contactId"
      :top="editBoardPosition.top"
      :left="editBoardPosition.left"
      :currentScrollLeft="editBoardPosition.currentScrollLeft"
      :currentScrollTop="editBoardPosition.currentScrollTop"
      @cancel="hideBoard"
    />
    </div>

    <!-- Modals and other components -->
    <new-conversation
      :show="isModalVisible"
      :contact="contactToDelete"
      @cancel="closeModal"
    />

    <empty-state
      v-if="!isLoading && showSearchEmptyState"
      :title="$t('CONTACTS_PAGE.LIST.404')"
    />
    
    <empty-state
      v-else-if="!isLoading && !contacts.length && !showSearchEmptyState"
      :title="$t('CONTACTS_PAGE.LIST.NO_CONTACTS')"
    />


    <new-edit-contact
      :show="showCreateModal"
      @cancel="onToggleEdit"
      :contact="contactToDelete"
    />

    <woot-delete-modal
      v-if="showDeleteModal"
      :show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />

    <call-modal
      :show="showDragmodal"
      :formatted="formattedTime"
      :hangup="CallHangup"
      :selectedContact="selectedContact"
    />
  </section>
</template>

<script>
import { getCountryFlag } from 'dashboard/helper/flag';
import EditState from './EditState.vue';
import EditStatus from './EditStatus.vue';
import EditBoard from './EditBoard.vue';
import Spinner from 'shared/components/Spinner.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import timeMixin from 'dashboard/mixins/time';
import rtlMixin from 'shared/mixins/rtlMixin';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon';
import LeadResponseWatch from 'dashboard/components/widgets/LeadResponseWatch.vue';
import NewConversation from '../../conversation/contact/NewConversation.vue';
import CallModal from 'dashboard/components/CallModal.vue';
import twilioCall from 'dashboard/mixins/twilioCall';
import NewEditContact from '../../conversation/contact/NewEditContact.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import LeadQualityCrown from 'dashboard/components/LeadQualityCrown.vue';
import {
  hasVisibleSocialProfiles,
  normalizeSocialProfiles,
} from './helpers/socialProfiles';

let token;
let device;
let call;

export default {
  components: {
    EmptyState,
    Spinner,
    NewConversation,
    EditState,
    EditStatus,
    CallModal,
    NewEditContact,
    EditBoard,
    Thumbnail,
    WootButton,
    FluentIcon,
    LeadResponseWatch,
    LeadQualityCrown,
  },
  mixins: [timeMixin, rtlMixin, twilioCall, alertMixin],
  directives: {
    // Update reactive cellTooltips so v-tooltip shows only when content is truncated
    tooltipWhenTruncated: {
      inserted(el, binding) {
        const update = () => {
          const b = el._tooltipWhenTruncatedBinding || binding;
          const payload = b.value && typeof b.value === 'object' ? b.value : { text: b.value, key: null };
          const text = payload.text != null ? String(payload.text) : '';
          const key = payload.key;
          let node = el;
          while (node && !node.__vue__) node = node.parentElement;
          let vm = node && node.__vue__;
          while (vm && !vm.cellTooltips && vm.$parent) vm = vm.$parent;
          if (!vm || !vm.cellTooltips || key == null) {
            el.title = '';
            return;
          }
          const truncated = text && el.scrollWidth > el.clientWidth;
          vm.$set(vm.cellTooltips, key, truncated ? text : '');
          el.title = '';
        };
        el._tooltipWhenTruncatedBinding = binding;
        el._tooltipWhenTruncatedUpdate = update;
        update();
        if (typeof ResizeObserver !== 'undefined') {
          el._tooltipWhenTruncatedRO = new ResizeObserver(update);
          el._tooltipWhenTruncatedRO.observe(el);
        }
      },
      update(el, binding) {
        el._tooltipWhenTruncatedBinding = binding;
      },
      componentUpdated(el, binding) {
        el._tooltipWhenTruncatedBinding = binding;
        if (el._tooltipWhenTruncatedUpdate) {
          el._tooltipWhenTruncatedUpdate();
        }
      },
      unbind(el) {
        if (el._tooltipWhenTruncatedRO) {
          el._tooltipWhenTruncatedRO.disconnect();
        }
      },
    },
  },
  props: {
    contacts: {
      type: Array,
      default: () => [],
    },
    showSearchEmptyState: {
      type: Boolean,
      default: false,
    },
    onClickContact: {
      type: Function,
      default: () => {},
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    activeContactId: {
      type: [String, Number],
      default: '',
    },
    sortParam: {
      type: String,
      default: 'created_at',
    },
    sortOrder: {
      type: String,
      default: 'desc',
    },
    visibleColumns: {
      type: Array,
      default: () => [],
    },
    columnOrder: {
      type: Array,
      default: () => [],
    },
    selectedContacts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      sortConfig: {},
      isModalVisible: false,
      showState: false,
      showStatus: false,
      showBoard: false,
      showDeleteModal: false,
      selectedRow: {},
      visibleActionsRowId: null,
      showCreateModal: false,
      statusId: null,
      stateId: null,
      contactId: null,
      boardId: null,
      contactToDelete: {},
      editBoardPosition: {
        top: 0,
        left: 0,
        left_scroll: 0,
        top_scroll: 0,
        editBoardPosition: 0,
        openUpward: false,
        isFirstOpen: true,
      },
      buttonRefs: {},
      dropdownRefs: {},
      selectedContact: null,
      timerRunning: false,
      timer: 0,
      seconds: 0,
      minutes: 0,
      hours: 0,
      interval: null,
      localSortParam: null,
      localSortOrder: null,
      sortUpdateKey: 0,
      hoveredRowId: null,
      cellTooltips: {},
      columnWidths: {
        name: 230,
        email: 230,
        phone_number: 192,
        created_at: 170,
        assigned_to: 170,
        source: 170,
        branch: 170,
        company: 170,
        city: 170,
        board: 170,
        status: 170,
        state: 170,
        country: 170,
        remark: 170,
        actions: 100,
      },
      columnMinWidth: 100,
      columnMaxWidth: 800,
      isResizing: false,
      resizingColumn: null,
      resizingColumnIndex: null,
      startX: 0,
      startWidth: 0,
    };
  },
  computed: {
    formattedTime() {
      return (
        this.pad(this.hours) +
        ':' +
        this.pad(this.minutes) +
        ':' +
        this.pad(this.seconds)
      );
    },
    ...mapGetters({
      CurrentAccountId: 'getCurrentAccountId',
      currentUserId: 'getCurrentUserID',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      allAttributes: 'attributes/getAttributes',
    }),
    // Current sort state - uses local state if set, otherwise falls back to props
    currentSortParam() {
      return this.localSortParam !== null ? this.localSortParam : this.sortParam;
    },
    currentSortOrder() {
      return this.localSortOrder !== null ? this.localSortOrder : this.sortOrder;
    },
    // Selection computed properties
    allContactsSelected() {
      return this.contacts.length > 0 && this.selectedContacts.length === this.contacts.length;
    },
    isIndeterminate() {
      return this.selectedContacts.length > 0 && this.selectedContacts.length < this.contacts.length;
    },
    tableData() {
      return this.contacts.map(item => {
        const custom = item.custom_attributes || {};
        const additional = item.additional_attributes || {};
        const { last_activity_at: lastActivityAt } = item;
        const { created_at: createdAt, reengaged_at: reengagedAt } = item;
        // Show reengaged_at in the "Created At" column when the contact has
        // been re-engaged. The original created_at is preserved separately
        // and surfaced via tooltip — there is intentionally no second column.
        const reengagementCount = Number(item.reengagement_count) || 0;
        const isReengaged = Boolean(reengagedAt) && reengagementCount > 0;
        const displayTimestamp = isReengaged ? reengagedAt : createdAt;

        const formatStamp = ts =>
          ts
            ? this.isCustomTimeStampOn
              ? this.formatTimestamp(ts)
              : this.dynamicTime(ts)
            : '---';

        const customAttributeData = {};
        this.customAttributeColumns.forEach(col => {
          const attrKey = col.attributeKey;
          customAttributeData[col.key] = custom[attrKey] ?? null;
        });

        return {
          ...item,
          phone_number: item.phone_number || '---',
          company: additional.company_name || '---',
          profiles: normalizeSocialProfiles(additional.social_profiles),
          city: additional.city || '---',
          state: additional.state || '---',
          status: additional.status || '---',
          assigned_to: item.assignee ? item.assignee.name : '---',
          remark: item.remark || '---',
          source: custom.source || '---',
          country: additional.country,
          countryCode: additional.country_code,
          conversationsCount: item.conversations_count || '---',
          last_activity_at: lastActivityAt
            ? this.dynamicTime(lastActivityAt)
            : '---',
          created_at: formatStamp(displayTimestamp),
          created_timing: formatStamp(displayTimestamp),
          original_created_at: formatStamp(createdAt),
          is_reengaged: isReengaged,
          reengagement_count: reengagementCount,
          branch: additional?.branch || '---',
          response_wait_waiting_since: item.response_wait_waiting_since ?? null,
          ...customAttributeData,
        };
      });
    },
    customAttributeColumns() {
      const contactAttributes =
        this.allAttributes.filter(
          attr => attr.attribute_model === 'contact_attribute'
        ) || [];
      return contactAttributes.map(attr => ({
        key: `custom_${attr.attribute_key}`,
        title: attr.attribute_display_name || attr.attribute_key,
        isCustomAttribute: true,
        attributeKey: attr.attribute_key,
        attributeType: attr.attribute_display_type,
      }));
    },
    allColumns() {
      const baseColumns = [
        {
          key: 'name',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.NAME'),
        },
        {
          key: 'email',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.EMAIL_ADDRESS'),
        },
        {
          key: 'phone_number',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.PHONE_NUMBER'),
        },
        {
          key: 'created_at',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.CREATED_AT'),
        },
        {
          key: 'assigned_to',
          title: 'Assigned To',
        },
        {
          key: 'source',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.SOURCE'),
        },
        // Conditionally show Branch OR Company based on feature flag
        ...(this.isMaacClientBranchFeatureEnabled ? [{
          key: 'branch',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.BRANCH'),
        }] : []),
        ...(this.isMaacClientBranchFeatureEnabled ? [] : [{
          key: 'company',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.COMPANY'),
        }]),
        {
          key: 'city',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.CITY'),
        },
        {
          key: 'board',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.BOARD'),
        },
        {
          key: 'status',
          title: 'PIPELINE',
        },
        {
          key: 'state',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.STATE'),
        },
        {
          key: 'country',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.COUNTRY'),
        },
        {
          key: 'remark',
          title: 'Social',
        },
      ];

      return [
        ...baseColumns,
        ...this.customAttributeColumns,
        {
          key: 'actions',
          title: this.$t('CONTACTS_PAGE.LIST.TABLE_HEADER.ACTIONS'),
        },
      ];
    },
    filteredColumnKeys() {
      const validKeys = new Set(this.allColumns.map(col => col.key));
      return this.columnOrder.filter(
        key => this.visibleColumns.includes(key) && validKeys.has(key)
      );
    },
    sortableColumns() {
      const baseColumns = [
        'name',
        'email',
        'phone_number',
        'created_at',
        'assigned_to',
        'source',
      ];
      
      // Conditionally add branch OR company
      const conditionalColumn = this.isMaacClientBranchFeatureEnabled ? 'branch' : 'company';
      
      const remainingColumns = [
        'city',
        'board',
        'status',
        'state',
        'country',
        'remark',
      ];

      return [...baseColumns, conditionalColumn, ...remainingColumns];
    },
    tableOptions() {
      return {
        perPage: 9999, // Show all rows (parent handles pagination)
        perPageValues: [],
        pagination: { show: false }, // Pagination handled by parent TableFooter component
        filterable: false,
        sortable: [], // Disable client-side sorting, we use server-side sorting via parent
        // orderBy: {
        //   column: this.sortParam,
        //   ascending: this.sortOrder === 'asc',
        // },
        headings: this.getHeadings(),
        columnsClasses: {
          name: 'name-column large-column',
          email: 'email-column large-column',
          phone_number: 'phone-column medium-column',
          created_at: 'medium-column',
          assigned_to: 'medium-column',
          source: 'medium-column',
          branch: 'medium-column',
          company: 'medium-column',
          city: 'medium-column',
          board: 'medium-column',
          status: 'medium-column',
          state: 'medium-column',
          country: 'medium-column',
          remark: 'medium-column',
          ...Object.fromEntries(
            this.customAttributeColumns.map(col => [col.key, 'medium-column'])
          ),
          actions: 'actions-column small-column',
        },
        skin: 'table table-bordered table-hover',
        texts: {
          count: 'Showing {from} to {to} of {count} contacts|{count} contacts|One contact',
          filter: 'Filter:',
          filterPlaceholder: 'Search...',
          limit: 'Records:',
          page: 'Page:',
          noResults: 'No matching records',
          loading: 'Loading...',
        },
        // We manage column visibility manually (custom implementation)
        columnsDropdown: false,  // Disable built-in
        saveState: false,        // We handle persistence manually
        resizableColumns: false,
      };
    },
    confirmDeleteMessage() {
      return ` ${this.contactToDelete.name}?`;
    },
    isCustomTimeStampOn() {
      const isAutoAssigne = this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.Account_TimeStamp
      );
      return isAutoAssigne;
    },
    isMaacClientBranchFeatureEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.MAAC_CLIENT_BRANCH);
    },
  },
  watch: {
    sortOrder() {
      this.setSortConfig();
    },
    sortParam() {
      this.setSortConfig();
    },
    showBoard(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    showStatus(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    showState(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    columnWidths: {
      handler() {
        this.$nextTick(() => {
          this.applyColumnWidths();
        });
      },
      deep: true,
    },
  },
  mounted() {
    this.setSortConfig();
    this.$store.dispatch('pipelines/getState');
    bus.$on('toggle-contact-heading-dropdown', this.toggleDropdownpipeline);
    this.$nextTick(() => {
      this.applyColumnWidths();
      this.initializeColumnResize();
    });
  },
  updated() {
    if (this.$refs.tableContainer && !this._scrollInitialized) {
      this.initialScrollLeft = this.$refs.tableContainer.scrollLeft;
      this.initialScrollTop = this.$refs.tableContainer.scrollTop;
      this._scrollInitialized = true;
    }
    this.$nextTick(() => {
      this.applyColumnWidths();
      this.initializeColumnResize();
    });
  },
  beforeDestroy() {
    document.body.style.overflow = '';
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('mousemove', this.handleResize);
    document.removeEventListener('mouseup', this.stopResize);
    document.body.classList.remove('resizing-column');
    bus.$off('toggle-contact-heading-dropdown', this.toggleDropdownpipeline);
  },
  methods: {
    getCountryFlag,
    getCellTooltip(key) {
      return this.cellTooltips[key] || '';
    },
    // Row hover methods for checkbox display
    isRowHovered(rowId) {
      return this.hoveredRowId === rowId;
    },
    onRowHover(rowId) {
      this.hoveredRowId = rowId;
    },
    onRowLeave(rowId) {
      // Only clear if leaving the currently hovered row
      if (this.hoveredRowId === rowId) {
        this.hoveredRowId = null;
      }
    },
    // Selection methods
    isContactSelected(contactId) {
      return this.selectedContacts.includes(contactId);
    },
    toggleContactSelection(contactId) {
      if (this.isContactSelected(contactId)) {
        this.$emit('deselect-contact', contactId);
      } else {
        this.$emit('select-contact', contactId);
      }
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        // Select all contacts on current page
        const allContactIds = this.contacts.map(contact => contact.id);
        this.$emit('select-all-contacts', allContactIds);
      } else {
        // Deselect all contacts
        this.$emit('deselect-all-contacts');
      }
    },
    getHeadings() {
      const headings = {};
      this.allColumns.forEach(col => {
        headings[col.key] = col.title;
      });
      return headings;
    },
    getDefaultColumnOrder() {
      // Return default column order based on feature flags
      return this.allColumns.map(col => col.key);
    },
    setSortConfig() {
      this.sortConfig = { [this.sortParam]: this.sortOrder };
    },
    toggleModal(row) {
      if (this.visibleActionsRowId === row.id) {
        this.closeModalAssign();
      } else {
        this.visibleActionsRowId = row.id;
        this.$nextTick(() => {
          setTimeout(() => {
            document.addEventListener('click', this.handleOutsideClick);
          }, 0);
        });
      }
    },
    closeModalAssign() {
      this.visibleActionsRowId = null;
    },
    handleOutsideClick(event) {
      if (!this.visibleActionsRowId) return;
      const dropdownRef = this.$refs[`dropdown_${this.visibleActionsRowId}`];
      const buttonRef = this.$refs[`button_${this.visibleActionsRowId}`];
      if (!dropdownRef || !buttonRef) return;
      const clickedInsideDropdown = dropdownRef.contains(event.target);
      const clickedInsideButton = buttonRef.contains(event.target);
      if (!clickedInsideDropdown && !clickedInsideButton) {
        this.closeModalAssign();
      }
    },
    openNewConversationModal(row) {
      if (!row?.id) return;
      this.contactToDelete = row;
      this.isModalVisible = true;
      this.visibleActionsRowId = null;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    hideState() {
      this.showState = false;
    },
    hideStatus() {
      this.showStatus = false;
    },
    hideBoard() {
      this.showBoard = false;
    },
    visibleStatus(status_id, board_id, contactId, event) {
      if (!board_id) {
        this.showAlert('Please select the board', { success: 'warning' });
        return;
      }
      this.showBoard = false;
      this.statusId = status_id;
      this.boardId = board_id;
      this.contactId = contactId;
      
      const scrollContainer = this.$refs.tableContainer;
      const currentScrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;
      const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
      
      // Get element position in viewport
      const rect = event.target.getBoundingClientRect();
      // Get container position in viewport
      const containerRect = scrollContainer.getBoundingClientRect();
 
      const modalHeight = 264;
      const modalWidth = 282;
      const isFirstOpen = true;

      // const spaceBelow = window.innerHeight - rect.bottom;
      // const spaceAbove = rect.top;
      // const spaceRight = window.innerWidth - rect.left;

      let top, left;
      let openUpward = false;

      // ✅ FIX: Calculate actual space INSIDE the container
 
        const spaceBelow = containerRect.bottom - rect.bottom;
        const spaceAbove = rect.top - containerRect.top;
        const spaceRight = window.innerWidth - rect.left;

      // Simple container-relative positioning (no scroll offset needed - absolute positioning handles it)
      if (spaceBelow < modalHeight && spaceAbove > modalHeight) {
        // Open upward
        top = (rect.top - containerRect.top) - modalHeight - 10;
        openUpward = true;
      } else {
        // Open downward
        top = (rect.bottom - containerRect.top) + 10;
      }

      // Calculate horizontal position
      if (spaceRight < modalWidth) {
        left = (rect.right - containerRect.left) - modalWidth - 5;
      } else {
        left = (rect.left - containerRect.left) + 5;
      }

      this.editBoardPosition = {
        top,
        left,
        openUpward,
        left_scroll: 0,
        top_scroll: 0,
        currentScrollLeft,
        currentScrollTop,
        isFirstOpen,
      };

      this.showStatus = false;
      this.$nextTick(() => {
        this.showStatus = true;
      });
    },
    visibleBoard(board_id, contactId, event) {
      this.showStatus = false;
      this.boardId = board_id;
      this.contactId = contactId;
      
      const scrollContainer = this.$refs.tableContainer;
      const currentScrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;
      const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;

      // Get element position in viewport
      const rect = event.target.getBoundingClientRect();
      // Get container position in viewport
      const containerRect = scrollContainer.getBoundingClientRect();
      
      const modalHeight = 264;
      const modalWidth = 282;
      const isFirstOpen = true;

      // const spaceBelow = window.innerHeight - rect.bottom;
      // const spaceAbove = rect.top;
      // const spaceRight = window.innerWidth - rect.left;

      let top, left;
      let openUpward = false;

      // ✅ FIX: Calculate actual space INSIDE the container

      const spaceBelow = containerRect.bottom - rect.bottom;
      const spaceAbove = rect.top - containerRect.top;
      const spaceRight = window.innerWidth - rect.left;

      // Simple container-relative positioning (no scroll offset needed - absolute positioning handles it)
      if (spaceBelow < modalHeight && spaceAbove > modalHeight) {
        // Open upward
        top = (rect.top - containerRect.top) - modalHeight - 10;
        openUpward = true;
      } else {
        // Open downward
        top = (rect.bottom - containerRect.top) + 10;
      }

      // Calculate horizontal position
      if (spaceRight < modalWidth) {
        left = (rect.right - containerRect.left) - modalWidth - 5;
      } else {
        left = (rect.left - containerRect.left) + 5;
      }

      this.editBoardPosition = {
        top,
        left,
        left_scroll: 0,
        top_scroll: 0,
        currentScrollLeft,
        currentScrollTop,
        openUpward,
        isFirstOpen,
      };

      this.showBoard = false;
      this.$nextTick(() => {
        this.showBoard = true;
      });
    },
    visibleState(state_id, contactId, event) {
      this.showState = false;
      this.stateId = state_id;
      this.contactId = contactId;
      
      const scrollContainer = this.$refs.tableContainer;
      const currentScrollLeft = scrollContainer ? scrollContainer.scrollLeft : 0;
      const currentScrollTop = scrollContainer ? scrollContainer.scrollTop : 0;
      
      // Get element position in viewport
      const rect = event.target.getBoundingClientRect();
      // Get container position in viewport
      const containerRect = scrollContainer.getBoundingClientRect();
      
      const modalHeight = 264;
      const modalWidth = 282;
      const isFirstOpen = true;

      // const spaceBelow = window.innerHeight - rect.bottom;
      // const spaceAbove = rect.top;
      // const spaceRight = window.innerWidth - rect.left;

      let top, left;
      let openUpward = false;

      // ✅ FIX: Calculate actual space INSIDE the container

      const spaceBelow = containerRect.bottom - rect.bottom;
      const spaceAbove = rect.top - containerRect.top;
      const spaceRight = window.innerWidth - rect.left;

      // Simple container-relative positioning (no scroll offset needed - absolute positioning handles it)
      if (spaceBelow < modalHeight && spaceAbove > modalHeight) {
        // Open upward
        top = (rect.top - containerRect.top) - modalHeight - 10;
        openUpward = true;
      } else {
        // Open downward
        top = (rect.bottom - containerRect.top) + 10;
      }

      // Calculate horizontal position
      if (spaceRight < modalWidth) {
        left = (rect.right - containerRect.left) - modalWidth - 5;
      } else {
        left = (rect.left - containerRect.left) + 5;
      }

      this.editBoardPosition = {
        top,
        left,
        openUpward,
        left_scroll: 0,
        top_scroll: 0,
        currentScrollLeft,
        currentScrollTop,
        isFirstOpen,
      };

      this.showState = false;
      this.$nextTick(() => {
        this.showState = true;
      });
    },
    onToggleEdit() {
      this.showCreateModal = false;
    },
    OpenEditContact(row) {
      this.contactToDelete = row;
      this.showCreateModal = true;
      this.visibleActionsRowId = null;
    },
    async deleteContact(contactId) {
      await this.$store.dispatch('contacts/delete', contactId);
      bus.$emit('on-assign');
      bus.$emit('dropdown-data-contact', true);
      this.showAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'), {
        success: true,
      });
      this.$emit('contact-deleted');
    },
    toggleDeleteModal(row) {
      this.contactToDelete = row;
      this.showDeleteModal = !this.showDeleteModal;
      this.visibleActionsRowId = null;
    },
    confirmDeletion() {
      this.deleteContact(this.contactToDelete.id);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
    },
    toggleDropdownpipeline() {
      this.closeModalAssign();
    },
    pad(value) {
      return value < 10 ? '0' + value : value;
    },
    closeOptions() {
      this.showOptions = false;
    },
    startTimer() {
      this.timerRunning = true;
      this.timer = setInterval(this.updateTimer, 1000);
    },
    stopTimer() {
      clearInterval(this.timer);
      this.timerRunning = false;
    },
    updateTimer() {
      this.seconds++;
      if (this.seconds === 60) {
        this.seconds = 0;
        this.minutes++;
        if (this.minutes === 60) {
          this.minutes = 0;
          this.hours++;
        }
      }
    },
    async updateUIAcceptedOutgoingCall() {
      this.startTimer();
    },
    async updateUIDisconnectedOutgoingCall() {
      alert('Call Ended');
      this.showDragmodal = false;
      this.resetTimer();
      window.call = null;
    },
    async intitializeDevice() {
      device = new Twilio.Device(token, {
        logLevel: 1,
      });
      this.addDeviceListeners(device);
      device.register();
    },
    async makeOutgoingCall() {
      try {
        const contact = this.selectedContact;
        var params = {
          To: contact.phone_number,
          account_id: this.CurrentAccountId,
          contact_id: contact.id,
          userId: this.currentUserId,
        };
        if (device) {
          call = await device.connect({ params });
          window.call = call;
          call.on('accept', this.updateUIAcceptedOutgoingCall);
          call.on('disconnect', this.updateUIDisconnectedOutgoingCall);
          call.on('cancel', this.updateUIDisconnectedOutgoingCall);
        } else {
          console.log('Unable to make call.');
        }
      } catch (error) {
        console.error('Error making outgoing call:', error);
      }
    },
    async startupClient(id) {
      const contactPhone = (id && id.phone_number ? id.phone_number : '')
        .toString()
        .trim();
      if (!contactPhone) {
        this.showAlert(this.$t('CONTACT_PANEL.DOMESTIC_CALL_PHONE_REQUIRED'));
        return;
      }

      const contact = id;
      this.selectedContact = contact;
      let data;
      
      // Request microphone permission FIRST before opening modal
      const hasPermission = await this.getAudioDevices();
      if (!hasPermission) {
        this.showAlert('Please allow access to your microphone in the browser to make a call.'); 
        return;
      }
      
      try {
        this.$store.dispatch('contacts/toggleholdCallAvailableStatusTrue');
        this.showDragmodal = !this.showDragmodal;
        const response = await axios.get(`${this.baseUrl}/twilio/token`, {
          params: {
            To: contact.phone_number,
            account_id: this.accountId,
            user_id: this.currentUserId,
          },
        });
        data = response.data;

        token = data.token;
        await this.intitializeDevice();
        this.makeOutgoingCall();
      } catch (err) {
        alert(data.message);
        this.showDragmodal = !this.showDragmodal;
        console.log('An error occurred. See your browser console for more information.');
      }
    },
    formatTimestamp(timestamp) {
      const date = new Date(timestamp * 1000);

      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date
        .getFullYear()
        .toString()
        .slice(-2);
      const hours = date.getHours() % 12 || 12;
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
      return `${day}${month}${year}, ${hours}:${minutes} ${ampm}`;
    },
    // Tooltip shown on the "Created At" cell for re-engaged contacts so users
    // understand why the row is above newer contacts. Surfaces the original
    // created_at and the count.
    buildReengagementTooltip(row) {
      return this.$t('CONTACTS_PAGE.LIST.REENGAGEMENT_TOOLTIP', {
        original: row.original_created_at || '---',
        count: row.reengagement_count,
      });
    },
    contactDisplayName(name) {
      const value = name == null ? '' : String(name).trim();
      if (!value) return '---';
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    hasVisibleSocialProfiles,
    getColumnTitle(columnKey) {
      const column = this.allColumns.find(col => col.key === columnKey);
      return column ? column.title : columnKey;
    },
    formatCustomAttributeValue(value, attributeType) {
      if (value === null || value === undefined) {
        return '---';
      }

      switch (attributeType) {
        case 'date':
          return new Date(value).toLocaleDateString();
        case 'checkbox':
          return value ? 'Yes' : 'No';
        case 'list':
          return Array.isArray(value) ? value.join(', ') : value;
        default:
          return String(value);
      }
    },
    triggerSort(column) {
      let newOrder = 'asc';
      if (this.currentSortParam === column) {
        newOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc';
      }
      
      this.localSortParam = column;
      this.localSortOrder = newOrder;
      this.sortUpdateKey++;
      
      this.$emit('on-sort-change', { [column]: newOrder });
    },
    initializeColumnResize() {
      const table = this.$refs.tableContainer?.querySelector('table');
      if (!table) return;

      const headers = table.querySelectorAll('thead th');
      
      table.querySelectorAll('.resize-handle').forEach(handle => handle.remove());
      
      headers.forEach((th, index) => {
        if (index === 0) return;

        const leftColumnKey = this.filteredColumnKeys[index - 1];
        if (leftColumnKey === 'actions') return;
        
        const leftTh = headers[index - 1];
        
        const resizeHandle = document.createElement('div');
        resizeHandle.className = 'resize-handle';
        resizeHandle.dataset.columnIndex = index - 1;
        resizeHandle.dataset.columnKey = leftColumnKey;
        resizeHandle.addEventListener('mousedown', (e) => this.startResize(e, leftColumnKey, leftTh, index - 1));
        
        th.style.position = 'relative';
        th.insertBefore(resizeHandle, th.firstChild);
      });
      
      this.applyColumnWidths();
    },
    startResize(e, columnKey, th, columnIndex) {
      e.preventDefault();
      e.stopPropagation();
      
      this.isResizing = true;
      this.resizingColumn = columnKey;
      this.resizingColumnIndex = columnIndex;
      this.startX = e.pageX;
      this.startWidth = this.columnWidths[columnKey] ?? th.offsetWidth;

      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.stopResize);
      document.body.classList.add('resizing-column');
    },
    handleResize(e) {
      if (!this.isResizing || !this.resizingColumn) return;

      const diff = e.pageX - this.startX;
      const newWidth = Math.max(
        this.columnMinWidth,
        Math.min(this.columnMaxWidth, this.startWidth + diff)
      );
      
      this.$set(this.columnWidths, this.resizingColumn, newWidth);
      
      const table = this.$refs.tableContainer?.querySelector('table');
      if (!table) return;

      const headers = table.querySelectorAll('thead th');
      const rows = table.querySelectorAll('tbody tr');
      const index = this.resizingColumnIndex;

      if (headers[index]) {
        headers[index].style.width = `${newWidth}px`;
        headers[index].style.minWidth = `${newWidth}px`;
        headers[index].style.maxWidth = `${newWidth}px`;

        rows.forEach(row => {
          const cell = row.children[index];
          if (cell) {
            cell.style.width = `${newWidth}px`;
            cell.style.minWidth = `${newWidth}px`;
            cell.style.maxWidth = `${newWidth}px`;
          }
        });
      }
      
      let totalWidth = 0;
      this.filteredColumnKeys.forEach((key) => {
        totalWidth += this.columnWidths[key] || 0;
      });
      table.style.width = `${totalWidth}px`;
    },
    stopResize() {
      this.isResizing = false;
      this.resizingColumn = null;
      this.resizingColumnIndex = null;
      
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.body.classList.remove('resizing-column');
    },
    applyColumnWidths() {
      const table = this.$refs.tableContainer?.querySelector('table');
      if (!table) return;

      const headers = table.querySelectorAll('thead th');
      const rows = table.querySelectorAll('tbody tr');
      const defaultColumnWidth = 170;

      let totalWidth = 0;
      this.filteredColumnKeys.forEach((columnKey, index) => {
        let width = this.columnWidths[columnKey];
        if (!width || width <= 0) {
          width = defaultColumnWidth;
          this.$set(this.columnWidths, columnKey, width);
        }
        if (headers[index]) {
          headers[index].style.width = `${width}px`;
          headers[index].style.minWidth = `${width}px`;
          headers[index].style.maxWidth = `${width}px`;

          rows.forEach(row => {
            const cell = row.children[index];
            if (cell) {
              cell.style.width = `${width}px`;
              cell.style.minWidth = `${width}px`;
              cell.style.maxWidth = `${width}px`;
            }
          });

          totalWidth += width;
        }
      });
      
      table.style.width = `${totalWidth}px`;
    },
  },
};
</script>

<style lang="scss" scoped>
.contacts-table-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; // Important for flex container with overflow
}

.table-container {
  flex: 1; 
  overflow: auto;
  max-height: calc(100vh - 6rem);
  position: relative;
  
  scroll-behavior: smooth;

    //Custom scrollbar styling
    &::-webkit-scrollbar {
     width: 8px;
     height: 8px;
   }

   &::-webkit-scrollbar-track {
     background: #f1f1f1;
     border-radius: 4px;
   }

   &::-webkit-scrollbar-thumb {
     background: #888;
     border-radius: 4px;
    
     &:hover {
       background: #555;
     }
   }
}

  ::v-deep .VueTables {
    width: 100%;
 
    .table {
      border-collapse: separate;
      border-spacing: 0;
      background-color: white;
      overflow: visible !important;
      table-layout: fixed !important;
    }

    .VueTables__table {
      margin-bottom: 0;
      overflow: visible !important;
    }

    .table-responsive {
      overflow: visible;
    }

    th {
      padding: var(--space-small) var(--space-two) !important;
      font-size: var(--font-size-mini) !important;
      background-color: #f9fafb;
      border-bottom: 1px solid #cfd2d6;
      border-right: 1px solid #cfd2d6;
      border-top: 1px solid #cfd2d6;
      text-align: left;
      font-weight: 600;
      color:  color(display-p3 0.6 0.6 0.6);
      color:  rgb(153, 153, 153);
      white-space: nowrap;
      position: sticky !important;
      top: 0 !important;
      z-index: 2 !important;
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      box-sizing: border-box;
      
      .custom-header-wrapper {
        width: 100%;
        overflow: hidden;
        
        .header-text {
          display: inline-block;
          user-select: none;
          pointer-events: none;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }
        
        .sort-icon-wrapper {
          display: inline-flex;
          align-items: center;
          cursor: pointer;
          padding: 2px 6px;
          border-radius: 3px;
          transition: background-color 0.2s;
          pointer-events: auto;
          flex-shrink: 0;
          
          &:hover {
            background-color: rgba(0, 0, 0, 0.1);
          }
          
          .sort-icon {
            font-size: 0.875rem;
            line-height: 1;
            color: #9ca3af;
            
            &::after {
              content: '⇅';
              position: relative;
              top: -0.125rem;
            }
          }
          
          &.is-sorted-asc .sort-icon {
            color: #3b82f6;
            font-weight: bold;
            
            &::after {
              content: '↑';
            }
          }
          
          &.is-sorted-desc .sort-icon {
            color: #3b82f6;
            font-weight: bold;
            
            &::after {
              content: '↓';
            }
          }
        }
      }
    }

    td {
      padding: var(--space-small) var(--space-two) !important;
      border-bottom: 1px solid #cfd2d6;
      border-right: 1px solid #cfd2d6;
      vertical-align: middle;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13px;
      line-height: 1.3;
      color:  color(display-p3 0.2 0.2 0.2);
      box-sizing: border-box;
      
      > div {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
    }

//     th,
// td {
//   width: 180px !important; // 👈 fixed width for all columns
//   min-width: 160px !important;
//   max-width: 180px !important;
//   overflow: hidden;
//   text-overflow: ellipsis;
//   white-space: nowrap;
// }

    tbody th,
    tbody td {
      padding: .151rem 1.25rem !important;
         line-height: 1.3;
    }


    tbody tr:hover td {
      background-color: #f9fafb !important;
    }

    // Removed automatic vue-tables-2 sortable styles since we handle sorting via icon clicks only

    .VueTables__search,
    .VueTables__filter,
    .VueTables__limit,
    .VueTables__pagination,
    .VuePagination {
      display: none !important; // Hide internal pagination - handled by parent TableFooter
    }

    .VueTables__child-row-toggler {
      cursor: pointer;
      color: #3b82f6;
      
      &:hover {
        color: #2563eb;
      }
    }
  }

  // Fixed first column - Name column stays in place (outside scoped to target generated DOM)
  ::v-deep .table thead tr th:first-child {
    position: -webkit-sticky !important;
    position: sticky !important;
    left: 0 !important;
    z-index: 1 !important;
    background-color: #f9fafb !important;
    
    z-index: 5 !important;
  }

  ::v-deep .table tbody tr td:first-child {
    position: -webkit-sticky !important;
    position: sticky !important;
    left: 0 !important;
    z-index: 2 !important;
    background-color: white !important;
 
  }

  // Hover effect for sticky first column
  ::v-deep .table tbody tr:hover td:first-child {
    background-color: #f9fafb !important;
  }

  // Resize handle for columns (positioned on left border, resizes left column)
  ::v-deep .resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    cursor: col-resize;
    user-select: none;
    z-index: 10;
    background-color: transparent;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #3b82f6;
    }
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -4px;
      width: 9px;
      height: 100%;
    }
  }
  
  // Prevent text selection during resize
  body.resizing-column {
    cursor: col-resize !important;
    user-select: none !important;
  }

  // Column width categories - now dynamic based on columnWidths data
  ::v-deep .table .large-column {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .table .medium-column {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .table .phone-column.medium-column {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ::v-deep .table .small-column {
    width: 100px !important;
    min-width: 100px !important;
    max-width: 100px !important;
  }

  /* Allow action dropdown to render outside the cell bounds. */
  ::v-deep .table td.actions-column {
    overflow: visible !important;
  }

  ::v-deep .table td.actions-column > div {
    overflow: visible !important;
    z-index: 0 !important;
  }


  // Name cell wrapper with checkbox on hover
  .name-cell-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
    
    .checkbox-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      cursor: pointer;
      transition: background-color 0.2s;
      flex-shrink: 0;
      
      &:hover {
        background-color: rgba(59, 130, 246, 0.1);
      }
      
      .checkbox {
        cursor: pointer;
        width: 16px;
        height: 16px;
        margin: 0;
      }
    }
    
    .contact-thumbnail {
      flex-shrink: 0;
    }
    
    .name-cell-button {
      padding: 0 !important;
      margin: 0 !important;
      height: 2.2rem !important;
      overflow: hidden;
      min-width: 0;
      
      .user-block {
        @apply items-center flex flex-row my-0 gap-1;
        
        .user-name {
          @apply font-medium m-0 capitalize flex-1 min-w-0;
          font-size: 13px !important;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  .row--user-block {
    @apply items-center flex text-left;

    .user-block {
      @apply items-start flex flex-col my-0 mx-2;
    }

    .user-name {
      @apply font-medium m-0 capitalize;
      font-size: 13px !important;
    }

    .view-details--button {
      @apply text-slate-600 dark:text-slate-200;
    }

    .user-email {
      @apply m-0;
    }
  }

  // Email column specific styling
  ::v-deep .email-column {
    a {
      font-size: 13px !important;
    }
  }


.cell--social-profiles {
  a {
    @apply text-lg min-w-[2rem] text-center;
  }
}

::v-deep .button-background {
  padding: 5px;
  border-radius: 50%;

  &:hover {
    background: #e5e5e5;
  }
}

::v-deep .light-grey {
  color: #aaaaaa;
}


::v-deep .background{
  background: #f3f4f6;
  border: 1px solid #d1d5db; 
  padding: 5px;
  display: inline-block;
  max-width: 100%;
  line-height: 0.7;
  vertical-align: middle
}

::v-deep .state-color {
  color: red;
  background: #ffebeb;
  padding: 5px;
  display: inline-block;
  max-width: 100%;
  line-height: 0.7;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .status-color {
  color: blue;
  background: #f6f6ff;
  padding: 5px;
  display: inline-block;
  max-width: 100%;
  line-height: 0.7;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .status-board {
  color: blue;
  background: #f6f6ff;
  padding: 5px;
  display: inline-block; 
  max-width: 100%;
  line-height: 0.7;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .ruby-div {
  display: flex;
  align-items: center;
  height: 100%;
}

::v-deep .dropdown-position {
  border-radius: 5px;
  text-align: center;
  min-width: 100px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  box-shadow: 0 8px 16px #00000033;
  position: absolute;
 right: 100%; // ← Change from 48px to 100% (left of button)
  margin-right: 8px; // ← Add margin for spacing
  top: -2px;
  transform: translateX(-8px);
  z-index: 2;
}

::v-deep .blue-color {
  color: #007ff6;
}

.edit-board,
.edit-status,
.edit-state {
  z-index: 9999;
}

::v-deep .edit-board,
::v-deep .edit-status,
::v-deep .edit-state {
  position: absolute ;
  max-width: 100vw;
  max-height: 100vh;
  overflow: auto;
  z-index: 9999;
}

.contacts-table-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
}

// Dark mode support
.dark {

  .contacts-table-loader-overlay {
    background: rgba(15, 23, 42, 0.78);
  }


  .contacts-table-wrap {
    ::v-deep .VueTables {
      .table {
        background-color: #151718;
      }

      th {
        background-color: #26292B;
        color: #e2e8f0;
        border-bottom-color: #475569;
        border-right-color: #475569;
        border-top-color: #475569;
      }

      td {
        color: #cbd5e1;
        border-bottom-color: #475569;
        border-right-color: #475569;
      }

      tbody tr:hover td {
        background-color: #334155 !important;
      }

      // Dark mode for custom header wrapper
      th .custom-header-wrapper .sort-icon-wrapper {
        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        .sort-icon {
          color: #94a3b8;
          
          // Default unsorted icon for dark mode
          &::after {
            content: '⇅';
          }
        }
        
        // Dark mode for sorted columns - ascending
        &.is-sorted-asc .sort-icon {
          color: #60a5fa;
          
          &::after {
            content: '↑';
          }
        }
        
        // Dark mode for sorted columns - descending
        &.is-sorted-desc .sort-icon {
          color: #60a5fa;
          
          &::after {
            content: '↓';
          }
        }
      }
    }

    // Fixed column dark mode (outside to target generated DOM)
    ::v-deep .table thead tr th:first-child {
      background-color: #26292B !important;
    }

    ::v-deep .table tbody tr td:first-child {
      background-color: #151718 !important;
    }

    // Hover effect for sticky first column in dark mode
    ::v-deep .table tbody tr:hover td:first-child {
      background-color: #334155 !important;
    }

    // Dark mode for background styled elements
    ::v-deep .background {
      background: transparent;
      border-color: #475569;
    }

    // Dark mode for resize handle
    ::v-deep .resize-handle {
      &:hover {
        background-color: #60a5fa;
      }
    }
  }
}
</style>
